import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { NotificationMessage, NotificationProps } from "../shared/models/models";
import { ResetPasswordModel } from "./models/AccountModels";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { resetPassword } from "./api/accountApi";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";

interface ResetPasswordState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage;
  openSnackbar: boolean;
  alertMessage: string;
  severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
  showPassword: boolean;
  showConfirmPassword: boolean;
}

const initialState: ResetPasswordState = {
  isLoading: false,
  formNotificationMessage: undefined,
  openSnackbar: false,
  alertMessage: "",
  severity: "error",
  showPassword: false,
  showConfirmPassword: false,
};

export const resetUserPassword = createAsyncThunk(
  "resetPassword/resetUserPassword",
  async (model: ResetPasswordModel, { rejectWithValue }) => {
    try {
      await resetPassword(model);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const resetPasswordSlice = createSlice({
  name: "resetPassword",
  initialState,
  reducers: {
    showNotification: (state, action: PayloadAction<NotificationProps>) => {
      state.openSnackbar = action.payload.openSnackbar;
      state.severity = action.payload.severity;
      state.alertMessage = action.payload.alertMessage;
    },
    setOpenSnackbar: (state, action: PayloadAction<boolean>) => {
      state.openSnackbar = action.payload;
    },
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    toggleConfirmPasswordVisibility: (state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    },
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetUserPassword.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = undefined;
      })
      .addCase(resetUserPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      });
  },
});

export const {
  showNotification,
  setOpenSnackbar,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  setFormNotificationMessage,
} = resetPasswordSlice.actions;

export default resetPasswordSlice.reducer; 